import React, { useState, useEffect } from "react";
import { Button, Progress, message, Spin } from "antd";
import Section1 from "./sections/Section1";
import Section2 from "./sections/Section2";
import Section3 from "./sections/Section3";
import Section4 from "./sections/Section4";
import Section5 from "./sections/Section5";
import Section6 from "./sections/Section6";
import Section7 from "./sections/Section7";
import Section8 from "./sections/Section8";
import ThankYouScreen from "./ThankYouScreen";
import { doRegistration } from "../../Api/RegistrationApis";
import { update, getUser } from "../../Api/Apis";
import AppConfig from "../config/AppConfig";
import { Auth } from "aws-amplify";
import "./MediaOutletSignupForm.css";

const sectionTitles = [
  "Section 1 - Agency Profile",
  "Section 2 - Service Regions & Local Reach",
  "Section 3 - Services Offered",
  "Section 4 - Core Specialties & Capabilities",
  "Section 5 - Audience & Cultural Expertise",
  "Section 6 - Industry Focus & Proof of Work",
  "Section 7 - Performance Measurement & Reporting",
  "Section 8 - Budget & References",
];

const sections = [
  Section1,
  Section2,
  Section3,
  Section4,
  Section5,
  Section6,
  Section7,
  Section8,
];

const MediaOutletSignupForm = () => {
  const [step, setStep] = useState(0);
  const [formValues, setFormValues] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("MediaPrefill");
      const goto = localStorage.getItem("MediaGotoStep");
      if (raw) {
        const prefill = JSON.parse(raw);
        if (prefill && typeof prefill === "object") {
          setFormValues((prev) => ({ ...prev, ...prefill }));
          if (prefill.id) localStorage.setItem("id", String(prefill.id)); // <-- add this
        }
      }
      if (goto) {
        const n = parseInt(goto, 10);
        if (!isNaN(n)) setStep(n);
      }
    } catch (e) {
      console.warn("Media prefill parse error", e);
    } finally {
      localStorage.removeItem("MediaPrefill");
      localStorage.removeItem("MediaGotoStep");
    }
  }, []);


  const CurrentSection = sections[step];
  const progressPercent = ((step + 1) / sections.length) * 100;

  // ---- helpers (spec logic) ----
  const allSelectedServices = () => [
    ...(formValues.creativeProduction || []),
    ...(formValues.digitalMarketing || []),
    ...(formValues.advertisingChannels || []),
    ...(formValues.publicEngagement || []),
  ];

  const coreIsSubsetOfServices = () => {
    const all = allSelectedServices().map((x) =>
      x.trim().toLowerCase()
    );

    return (formValues.coreSpecialties || []).every((s) => {
      if (s === "Other (please specify)") return true;

      return all.includes(s.trim().toLowerCase());
    });
  };

  const topsAreSubsetOfIndustries = () => {
    const base = formValues.industries || [];
    const top = formValues.topIndustries || [];
    return top.every((t) => base.includes(t));
  };

  // Build post-submission flags per spec
  const buildFlags = () => {
    const flags = [];

    // Service vs Specialty (Q17 ⊆ Q12–15)
    if (!coreIsSubsetOfServices()) {
      flags.push(
        "Core specialties include items not selected in Services Offered (Section 3)."
      );
    }

    // Region vs Local Reach
    if (
      (formValues.area || []).includes("All Connecticut") &&
      ["1%-25%", "0%"].includes(formValues.localWorkPercent)
    ) {
      flags.push(
        "Selected 'All Connecticut' but local work percentage is very low."
      );
    }

    // Proof of Work zeros
    (formValues.coreSpecialties || []).forEach((svc) => {
      (formValues.topIndustries || []).forEach((ind) => {
        const v = formValues.proofOfWork?.[svc]?.[ind];
        if (v === "0") flags.push(`PoW 0 campaigns for ${svc} × ${ind}.`);
      });
    });

    // Creative vs Campaign Execution
    if (formValues.campaignExecution === "We manage full campaign execution") {
      const mgmtSignals = ["Event Management", "CRM / Lead Management"];
      const hasMgmt = allSelectedServices().some((s) =>
        mgmtSignals.includes(s)
      );
      if (!hasMgmt)
        flags.push(
          "Claims full campaign execution but no management services selected."
        );
    }

    // Performance vs Capability
    if (
      (formValues.kpis || []).length > 0 &&
      (formValues.analyticsTools || []).includes(
        "We do not use any analytics tools"
      )
    ) {
      flags.push("Tracks KPIs but selected 'no analytics tools'.");
    }
    if (
      ["Weekly", "Biweekly"].includes(formValues.reportingFrequency) &&
      !(formValues.analyticsTools || []).length
    ) {
      flags.push("Frequent reporting but no analytics tools listed.");
    }

    return flags;
  };
  // ---- end helpers ----  

  const handleLogin = async (Username, Password, source = "manual") => {
    try {
      setLoggingIn(true);

      // 1) Sign in
      const user = await Auth.signIn(Username, Password);
      localStorage.setItem("user", JSON.stringify(user.username));

      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();
      localStorage.setItem("token", idToken);

      const keys = [user?.username, user?.attributes?.email, user?.attributes?.sub].filter(Boolean);
      let u = null;
      for (const k of keys) {
        try {
          const res = await getUser(AppConfig.entityNames.registrations, k);
          u = res?.data?.data || res?.data || null;
          if (res?.success && u) break;
        } catch (e) { }
      }

      if (!u) {
        message.error("MediaOutlet profile not found. Please sign up.");
        return false;
      }

      const candidateId =
        (user?.attributes?.email || "").trim() ||
        (user?.username || "").trim();

      localStorage.setItem("userid", JSON.stringify(u.id || candidateId));
      localStorage.setItem("loginDetails", JSON.stringify(u));
      localStorage.setItem("id", u.id);

      const mapRegistrationToForm = (u) => ({
        // Section 1
        businessName: u.businessName ?? "",
        contactName: u.contactPerson ?? "",
        title: u.title ?? "",
        email: u.email ?? "",
        phoneNumber: u.phoneNumber ?? "",
        address: u.address ?? "",
        city: u.city ?? "",
        stateName: u.place ?? "",
        state: u.place ?? "",
        zip: u.zip ?? "",
        businessEntityType: u.businessEntityType ?? "",
        vendorRegistration: u.vendorRegistration ?? "",
        smbCertification: u.smbCertification ?? "",
        id: u.id,

        // Section 2
        area: u.area ?? [],
        localWorkPercent: (u.localWorkPercent || "").replace(/-/g, "-").trim(),

        // Section 3
        creativeProduction: u.creativeProduction ?? [],
        digitalMarketing: u.digitalMarketing ?? [],
        advertisingChannels: u.advertisingChannels ?? [],
        publicEngagement: u.publicEngagement ?? [],
        otherServices: u.otherServices ?? "",
        creativeDevelopment: u.creativeDevelopment ?? "",
        creativeDevelopmentExplanation: u.creativeDevelopmentExplanation ?? "",

        // Section 4
        coreSpecialties: u.coreSpecialties ?? [],
        otherCoreSpecialty: u.otherCoreSpecialty ?? "",
        matchComfort: u.matchComfort ?? "",
        matchComfortExplanation: u.matchComfortExplanation ?? "",
        campaignExecution: u.campaignExecution ?? "",
        leadTime: u.leadTime ?? "",

        // Section 5
        ageGroups: Array.isArray(u.ageGroup)
          ? u.ageGroup.map((s) => String(s).replace(/-/g, "-").trim())
          : u.ageGroup
            ? [String(u.ageGroup).replace(/-/g, "-").trim()]
            : [],
        lifestyles: u.lifestyles ?? [],
        otherLifestyles: u.otherLifestyles ?? "",
        culturalGroups: u.culturalGroups ?? [],
        otherCulturalGroups: u.otherCulturalGroups ?? "",

        // Section 6
        industries: u.industry ?? [],
        topIndustries: u.topIndustries ?? [],
        proofOfWork: u.proofOfWork ?? {},
        otherIndustries: u.otherIndustries ?? "",

        // Section 7
        kpis: u.kpis ?? [],
        otherKpis: u.otherKpis ?? "",
        reportingFrequency: u.reportingFrequency ?? "",
        analyticsTools: u.analyticsTools ?? [],
        otherAnalyticsTools: u.otherAnalyticsTools ?? "",

        // Section 8
        budgetRange: (
          Array.isArray(u.ranges)
            ? u.ranges.reduce((acc, item) => acc.concat(Array.isArray(item) ? item : [item]), [])
            : u.ranges
              ? [u.ranges]
              : []
        ).map(s => String(s).replace(/-/g, "-").trim()).filter(Boolean),
        clientsBelow10k: u.clientsBelow10k ?? "",
        clientReferences: u.clientReferences ?? "",

        signedInExisting: true,
      });

      setFormValues((prev) => ({ ...prev, ...mapRegistrationToForm(u) }));

      if (source === "manual") {
        message.success("Signed in successfully.");
      }
      return true;
    } catch (error) {
      console.error("error signing in", error);
      message.error(error?.message || "Sign-in failed.");
      return false;
    } finally {
      setLoggingIn(false);
    }
  };



  const handleNext = async () => {
    if (step === 0 && formValues?.signedInExisting) {
      const id = formValues?.id || localStorage.getItem("id");
      if (id && !localStorage.getItem("id")) {
        localStorage.setItem("id", id);
      }
      setStep((s) => s + 1);
      return;
    }

    // normal validation
    const currentValidator = CurrentSection.validate;
    if (typeof currentValidator === "function") {
      const errors = currentValidator(formValues);
      setValidationErrors(errors);
      if (Object.keys(errors).length > 0) {
        message.error("Please resolve validation errors before proceeding.");
        return;
      }
    }

    // Step 1: Submit initial registration
    if (step === 0) {
      const section1Payload = {
        businessName: formValues.businessName || "",
        contactPerson: formValues.contactName || "",
        title: formValues.title || "",
        email: formValues.email || "",
        phoneNumber: formValues.phoneNumber || "",
        address: formValues.address || "",
        city: formValues.city || "",
        place: formValues.stateName || formValues.state || "",
        zip: formValues.zip || "",
        businessEntityType: formValues.businessEntityType || "",
        vendorRegistration: formValues.vendorRegistration || "",
        smbCertification: formValues.smbCertification || "",
      };

      const Data = { entityData: section1Payload };
      setIsLoading(true);

      try {
        const result = await doRegistration(
          AppConfig["entityNames"]["registrations"],
          Data
        );

        const msg = result?.data?.message || result?.message;
        const code = result?.data?.code || result?.code;
        const statusCode = result?.data?.statusCode;

        if (code === "UsernameExistsException" || statusCode === 0) {
          message.error(
            msg || "This business already exists, please verify/sign in."
          );
          setIsLoading(false);
          return;
        }

        const registrationId =
          result?.data?.data?.id || result?.data?.id || result?.id;

        if (result?.success === true || result?.data?.message === "Success") {
          localStorage.setItem("id", registrationId);
          message.success("Registration successful.");
          setFormValues((prev) => ({ ...prev, id: registrationId }));
          const email = Data.entityData.email;
          const tempPassword = result?.data?.password;
          if (email && tempPassword) {
            await handleLogin(email, tempPassword, "registration");
          }
          setStep((s) => s + 1);
          return;
        }

        message.error(msg || "Registration failed. Please check your details.");
      } catch (err) {
        message.error(
          err?.message || "Something went wrong during registration."
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Step 2: Save counties and local work percent
    if (step === 1) {
      const id = localStorage.getItem("id");
      if (!id) {
        message.error("Missing registration id. Please complete Section 1 again.");
        return;
      }

      if (!formValues.area || formValues.area.length === 0) {
        message.error("Please select at least one county in Section 2.");
        return;
      }

      if (!formValues.localWorkPercent) {
        message.error("Please select your work percentage in Section 2.");
        return;
      }
      setIsLoading(true);

      try {
        const payload = {
          entityData: {
            area: formValues.area,
            localWorkPercent: formValues.localWorkPercent,
          },
        };
        const response = await update(
          AppConfig["entityNames"]["registrations"],
          id,
          payload
        );

        if (response?.success === true || response?.data?.message === "Success") {
          setStep(step + 1);
        } else {
          message.error(
            " Failed to save Section 2. Please check your answers."
          );
        }
      } catch (err) {
        console.error("Section 2 update failed:", err);
        message.error("Something went wrong while updating Section 2.");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Step 3: Services Offered
    if (step === 2) {
      const id = localStorage.getItem("id");
      const hasSocial = (formValues.digitalMarketing || []).some((s) =>
        s.toLowerCase().includes("social media")
      );

      const platformFromAds = (formValues.advertisingChannels || []).map(
        (s) => {
          if (s.startsWith("TV")) return "TV";
          if (s === "Radio") return "Radio";
          if (s.startsWith("Print")) return "Print";
          if (s === "Digital Billboards/Posters") return "Digital Billboards";
          if (s === "Programmatic Advertising") return "Programmatic";
          if (s === "Direct Mail") return "Direct Mail";
          return s; // Geofencing, etc.
        }
      );

      const platform = Array.from(
        new Set([...platformFromAds, ...(hasSocial ? ["Social Media"] : [])])
      );
      const {
        creativeProduction,
        // digitalMarketing,
        // advertisingChannels,
        // publicEngagement,
        // otherServices,
        creativeDevelopment,
        // creativeDevelopmentExplanation,
      } = formValues;

      // Simple, readable validation
      // if (!creativeProduction?.length) {
      //   message.error(
      //     "Please select at least one Creative & Production service."
      //   );
      //   return;
      // }

      // if (!digitalMarketing?.length) {
      //   message.error("Please select at least one Digital Marketing service.");
      //   return;
      // }

      // if (!advertisingChannels?.length) {
      //   message.error("Please select at least one advertisingChannels.");
      //   return;
      // }

      // if (!publicEngagement?.length) {
      //   message.error("Please select at least one publicEngagement.");
      //   return;
      // }
      // // if (!otherServices?.length) {
      // //   message.error("Please fill otherServices.");
      // //   return;
      // // }
      // if (!creativeDevelopment?.length) {
      //   message.error("Please select creativeDevelopment.");
      //   return;
      // }

      // Q16 tip: warn if “Yes” but no Creative & Production types were selected
      // if (
      //   !creativeProduction?.length &&
      //   creativeDevelopment.startsWith("Yes")
      // ) {
      //   message.warning(
      //     "You said you do in-house creative development but didn't select any Creative & Production services above. Please confirm."
      //   );
      //   // choose to block by returning here if you want hard stop
      //   return;
      // }
      // if (!creativeDevelopmentExplanation?.length) {
      //   message.error("Please fill creativeDevelopmentExplanation.");
      //   return;
      // }

      const section3Data = {
        creativeProduction: formValues.creativeProduction || [],
        digitalMarketing: formValues.digitalMarketing || [],
        advertisingChannels: formValues.advertisingChannels || [],
        publicEngagement: formValues.publicEngagement || [],
        otherServices: formValues.otherServices || "",
        creativeDevelopment: formValues.creativeDevelopment || "",
        creativeDevelopmentExplanation:
          formValues.creativeDevelopmentExplanation || "",
        // 👇 old-project fields
        marketing: formValues.publicEngagement || [], // e.g. ["Public relations"]
        platform, // e.g. ["Radio","Social Media"]
        flags: buildFlags(),
      };
      setIsLoading(true);

      try {
        const payload = {
          entityData: section3Data,
        };

        const response = await update(
          AppConfig.entityNames.registrations,
          id,
          payload
        );

        if (response?.success) {
          setStep(step + 1);
        } else {
          message.error(
            "Failed to save Section 3. Please check your responses."
          );
        }
      } catch (err) {
        console.error("Section 3 update failed:", err);
        message.error("Something went wrong while saving Section 3.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 3) {
      const id = localStorage.getItem("id");
      if (!id) {
        message.error("Missing registration ID. Please complete Section 1 first.");
        return;
      }

      const {
        coreSpecialties,
        otherCoreSpecialty,
        matchComfort,
        matchComfortExplanation,
        campaignExecution,
        leadTime,
      } = formValues;

      // Basic validations
      // if (!coreSpecialties?.length) {
      //   message.error("Please select at least one Core Specialty.");
      //   return;
      // }

      const selectedServices = [
        ...(formValues.creativeProduction || []),
        ...(formValues.digitalMarketing || []),
        ...(formValues.advertisingChannels || []),
        ...(formValues.publicEngagement || []),
        ...(formValues.otherServices?.trim() ? [formValues.otherServices.trim()] : []),
      ];

      const invalidSpecialties = coreSpecialties.filter(
        (s) => !selectedServices.includes(s)
      );

      if (invalidSpecialties.length) {
        message.error("Core Specialties must be selected from services in Section 3.");
        return;
      }

      if (!matchComfort) {
        message.error("Please select your Match Comfort.");
        return;
      }

      if (matchComfort === "Depends" && !matchComfortExplanation?.trim()) {
        message.error("Please explain your Match Comfort.");
        return;
      }

      if (!campaignExecution) {
        message.error("Please fill Campaign Execution.");
        return;
      }

      if (!leadTime) {
        message.error("Please fill Lead Time.");
        return;
      }

      // Prepare payload
      const section4Payload = {
        coreSpecialties,
        ...(coreSpecialties.includes("Other (please specify)") && { otherCoreSpecialty }),
        matchComfort,
        ...(matchComfort === "Depends" && { matchComfortExplanation }),
        campaignExecution,
        leadTime,
        flags: buildFlags(),
      };

      setIsLoading(true);
      try {
        const response = await update(
          AppConfig.entityNames.registrations,
          id,
          { entityData: section4Payload }
        );

        if (response?.success) {
          setStep(step + 1);
        } else {
          message.error("Failed to save Section 4. Please check your answers.");
        }
      } catch (err) {
        console.error("Section 4 update failed:", err);
        message.error("Something went wrong while saving Section 4.");
      } finally {
        setIsLoading(false);
      }

      return;
    }


    if (step === 4) {
      const id = localStorage.getItem("id");
      const {
        ageGroups,
        lifestyles,
        otherLifestyles,
        culturalGroups,
        otherCulturalGroups,
      } = formValues;

      // Basic validations
      if (!ageGroups || ageGroups.length === 0) {
        message.error("Please select at least one Age Group.");
        return;
      }

      if (!lifestyles || lifestyles.length === 0) {
        message.error("Please select at least one Lifestyle.");
        return;
      }

      if (lifestyles.includes("Other") && !otherLifestyles) {
        message.error("Please specify other Lifestyle.");
        return;
      }

      if (!culturalGroups || culturalGroups.length === 0) {
        message.error("Please select at least one Cultural Group.");
        return;
      }

      if (culturalGroups.includes("Other") && !otherCulturalGroups) {
        message.error("Please specify other Cultural Group.");
        return;
      }
      const fixDash = (s) => (s || "").replace(/-/g, "-");
      setIsLoading(true);
      const payload = {
        entityData: {
          ageGroup: (formValues.ageGroups || []).map(fixDash),
          lifestyles: formValues.lifestyles,
          otherLifestyles: formValues.otherLifestyles,
          culturalGroups: formValues.culturalGroups,
          otherCulturalGroups: formValues.otherCulturalGroups,
          flags: buildFlags(),
        },
      };

      try {
        const response = await update(
          AppConfig.entityNames.registrations,
          id,
          payload
        );
        if (response?.success) {
          setStep(step + 1);
        } else {
          message.error("Failed to save Section 5. Please check your answers.");
        }
      } catch (err) {
        console.error("Section 5 update failed:", err);
        message.error("Something went wrong while saving Section 5.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 5) {
      const id = localStorage.getItem("id");
      const { industries, topIndustries, proofOfWork, otherIndustries } =
        formValues;

      // ✅ Validations
      if (!industries || industries.length === 0) {
        message.error("Please select at least one Industry.");
        return;
      }

      if (!topIndustries || topIndustries.length === 0) {
        message.error("Please select your top industries.");
        return;
      }
      if (!topsAreSubsetOfIndustries())
        return message.error(
          "Top industries (Q25) must come from the industries you selected in Q24."
        );

      if (
        industries.includes("Other") &&
        (!otherIndustries || otherIndustries.trim() === "")
      ) {
        message.error("Please specify your Other Industry.");
        return;
      }

      if (!proofOfWork || Object.keys(proofOfWork).length === 0) {
        message.error("Please fill out the Proof of Work table.");
        return;
      }
      setIsLoading(true);

      const payload = {
        entityData: {
          industry: formValues.industries || [],
          topIndustries: formValues.topIndustries || [],
          proofOfWork: formValues.proofOfWork || {},
          ...(formValues.otherIndustries && {
            otherIndustries: formValues.otherIndustries,
          }),
          flags: buildFlags(),
        },
      };

      try {
        const response = await update(
          AppConfig.entityNames.registrations,
          id,
          payload
        );
        if (response?.success) {
          setStep(step + 1); // Proceed to Section 7
        } else {
          message.error("Failed to save Section 6. Please check your inputs.");
        }
      } catch (err) {
        console.error("Section 6 update failed:", err);
        message.error("Something went wrong while saving Section 6.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 6) {
      const id = localStorage.getItem("id");
      setIsLoading(true);
      const {
        kpis,
        otherKpis,
        reportingFrequency,
        analyticsTools,
        otherAnalyticsTools,
      } = formValues;

      //  Validations
      if (!kpis || kpis.length === 0) {
        message.error("Please select at least one KPI.");
        setIsLoading(false);
        return;
      }

      if (kpis.includes("Other (please specify)") && !otherKpis?.trim()) {
        message.error("Please specify the other KPI.");
        setIsLoading(false);
        return;
      }

      if (!reportingFrequency) {
        message.error("Please select reporting frequency.");
        setIsLoading(false);
        return;
      }

      if (!analyticsTools || analyticsTools.length === 0) {
        message.error("Please select at least one analytics tool.");
        setIsLoading(false);
        return;
      }

      if (
        analyticsTools.includes("Other (please specify)") &&
        !otherAnalyticsTools?.trim()
      ) {
        message.error("Please specify the other analytics tool.");
        setIsLoading(false);
        return;
      }

      const payload = {
        entityData: {
          kpis: formValues.kpis || [],
          otherKpis: formValues.kpis?.includes("Other (please specify)")
            ? formValues.otherKpis || ""
            : undefined,
          reportingFrequency: formValues.reportingFrequency || "",
          analyticsTools: formValues.analyticsTools || [],
          otherAnalyticsTools: formValues.analyticsTools?.includes(
            "Other (please specify)"
          )
            ? formValues.otherAnalyticsTools || ""
            : undefined,
          flags: buildFlags(),
        },
      };

      try {
        const response = await update(
          AppConfig.entityNames.registrations,
          id,
          payload
        );

        if (response?.success) {
          setStep(step + 1); // Proceed to Section 8
        } else {
          message.error("Failed to save Section 7. Please check your answers.");
        }
      } catch (err) {
        console.error("Section 7 update failed:", err);
        message.error("Something went wrong while saving Section 7.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    if (step === 7) {
      const id = localStorage.getItem("id");
      setIsLoading(true);
      const { budgetRange, clientsBelow10k, clientReferences } = formValues;

      //  Validations
      if (!budgetRange) {
        message.error("Please select your average client budget range.");
        setIsLoading(false);
        return;
      }

      if (!clientsBelow10k) {
        message.error("Please indicate how many clients were below $10k.");
        setIsLoading(false);
        return;
      }

      if (!clientReferences || !clientReferences.trim()) {
        message.error("Please enter at least one client reference.");
        setIsLoading(false);
        return;
      }

      const payload = {
        entityData: {
          ranges: (formValues.budgetRange || []).flat(),
          // ranges: formValues.budgetRange ? [formValues.budgetRange] : [],
          clientsBelow10k: formValues.clientsBelow10k || "",
          clientReferences: formValues.clientReferences || "",
          flags: buildFlags(),
        },
      };
      if (formValues.budgetRange) {
        localStorage.setItem(
          "lastBusinessRangeGoals",
          JSON.stringify(formValues.budgetRange)
        );
      }

      try {
        const response = await update(
          AppConfig.entityNames.registrations,
          id,
          payload
        );

        if (response?.success) {
          message.success("Section 8 saved successfully.");
          setIsSubmitted(true); // Show Thank You screen
        } else {
          message.error("Failed to save Section 8. Please check your answers.");
        }
      } catch (err) {
        console.error("Section 8 update failed:", err);
        message.error("Something went wrong while saving Section 8.");
      } finally {
        setIsLoading(false);
      }

      return;
    }

    // Other sections
    setStep(step + 1);
  };

  const handleBack = () => setStep(step - 1);

  const handleSubmit = async () => {
    const id = localStorage.getItem("id");
    const normalizeRanges = (value) =>
      Array.isArray(value) ? value.flat() : value ? [value] : [];

    const payload = {
      entityData: {
        ranges: normalizeRanges(formValues.budgetRange),
        clientsBelow10k: formValues.clientsBelow10k || "",
        clientReferences: formValues.clientReferences || "",
        flags: buildFlags(),
      },
    };

    try {
      setIsLoading(true);
      const response = await update(
        AppConfig.entityNames.registrations,
        id,
        payload
      );

      if (response?.success) {
        setIsSubmitted(true); //  Show Thank You screen
      } else {
        message.error("Failed to submit. Please try again.");
      }
    } catch (err) {
      console.error(" Final submission error:", err);
      message.error("Something went wrong while submitting.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="media-signup-container">
      {isSubmitted ? (
        <ThankYouScreen data={{ businessName: formValues.businessName }} />
      ) : (
        <Spin spinning={isLoading} tip="Processing...">
          <h1 style={{ textAlign: "center" }}>Join as a Media Outlet</h1>

          <h2 style={{ marginBottom: "15px", color: "#444" }}>
            {sectionTitles[step]}
          </h2>

          <Progress percent={progressPercent} style={{ marginBottom: 20 }} />

          <CurrentSection
            formValues={formValues}
            setFormValues={setFormValues}
            validationErrors={validationErrors}
            setValidationErrors={setValidationErrors}
            onNext={handleNext}
            setIsLoading={setIsLoading} // pass down for Section2 API
            setStep={setStep}
            onLogin={handleLogin}
          />

          <div style={{ marginTop: 20 }}>
            {step > 0 && (
              <Button style={{ marginRight: 8 }} onClick={handleBack}>
                Back
              </Button>
            )}

            {step < sections.length - 1 && (
              <Button type="primary" onClick={handleNext}>
                Next
              </Button>
            )}

            {step === sections.length - 1 && (
              <Button type="primary" onClick={handleSubmit}>
                Submit
              </Button>
            )}
          </div>
        </Spin>
      )}
    </div>
  );
};

export default MediaOutletSignupForm;
